'use client';

import { forwardRef } from 'react';
import { Avatar as MantineAvatar } from '@mantine/core';
import type {
  AvatarGroupProps as MantineAvatarGroupProps,
  AvatarProps as MantineAvatarProps,
  AvatarVariant,
  ElementProps,
} from '@mantine/core';

import type { LockedProps } from '../types';

// Mantine's AvatarProps carries none of the div's own ARIA props (it's polymorphic). Intersect
// them the way CloseButton does for button handlers. Placeholder avatars render a `div`, which
// ignores `aria-label` until it has a role; `img` is how a name-only avatar exposes that label.
export type AvatarProps = Omit<MantineAvatarProps, LockedProps | 'variant'> &
  Omit<ElementProps<'div'>, LockedProps | 'variant' | 'color'> & {
    /**
     * `menu` is the top-nav treatment for the logged-in user (see the theme's `Avatar` entry).
     * `tinted` is the soft -2 fill inside a -3 ring that Facepile uses, in the avatar's `color`.
     * Mantine types `variant` as an open union, so it is re-narrowed here to keep a typo from
     * silently rendering an unskinned avatar.
     */
    variant?: AvatarVariant | 'menu' | 'tinted';
  };

/**
 * ### Avatar
 *
 * @description
 * Mantine's Avatar minus per-call-site style overrides (see {@link LockedProps}).
 * Pass `name` for the initials fallback when there is no `src`, and
 * `variant="menu"` for the top-nav treatment of the logged-in user.
 * `Avatar.Group` is Mantine's overlapping row for laying out several avatars.
 *
 * @example
 * <Avatar src={user.avatarUrl} alt={user.name} />
 * <Avatar name="Ada Lovelace" alt="Ada Lovelace" color="primary" />
 */
export type AvatarGroupProps = Omit<MantineAvatarGroupProps, LockedProps>;

const AvatarComponent = forwardRef<HTMLDivElement, AvatarProps>((props, ref) => (
  <MantineAvatar ref={ref} {...props} />
));
AvatarComponent.displayName = 'Avatar';

// Re-exporting `MantineAvatar.Group` handed callers the raw Mantine surface, so the lock on
// `Avatar` stopped at the root. Group only supplies overlap context, so a pass-through wrapper
// adds no extra DOM and the stack styling is unchanged.
const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>((props, ref) => (
  <MantineAvatar.Group ref={ref} {...props} />
));
AvatarGroup.displayName = 'Avatar.Group';

const Avatar = Object.assign(AvatarComponent, {
  Group: AvatarGroup,
});

export default Avatar;
